import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Search, Eye, Pencil, Trash2, Inbox } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { inr } from "@/lib/calc";
import { catLabel, subLabel, exportReceiptsXlsx } from "@/lib/export";
import { FileSpreadsheet } from "lucide-react";
import { StatusBadge } from "@/components/StatusSelect";
import { PaymentState } from "@/components/PaymentVerify";
import { verifyCount } from "@/lib/payments";

const stateKey = ({ n, total }) => (!total ? "none" : n === total ? "verified" : n === 0 ? "not" : "partly");
const PS_FILTERS = [{ id: "all", label: "All" }, { id: "not", label: "Not verified" }, { id: "partly", label: "Partly verified" }, { id: "verified", label: "Verified" }];

export default function History() {
  const nav = useNavigate();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [q, setQ] = useState("");
  const [ps, setPs] = useState("all");
  const { data = [], isLoading } = useQuery({ queryKey: ["receipts"], queryFn: () => api.get("/receipts").then((r) => r.data) });

  const rows = useMemo(() => {
    const t = q.toLowerCase();
    return data.filter((r) => [r.receipt_no, r.inputs.childName, r.inputs.parentName, r.inputs.grade, r.inputs.contact]
      .some((x) => (x || "").toLowerCase().includes(t)) && (ps === "all" || stateKey(verifyCount(r)) === ps));
  }, [data, q, ps]);

  const del = async (id) => {
    try {
      await api.delete(`/receipts/${id}`);
      qc.invalidateQueries({ queryKey: ["receipts"] });
      toast.success("Receipt deleted");
    } catch { toast.error("Could not delete receipt"); }
  };

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div className="fade-up">
          <div className="text-xs uppercase tracking-[0.2em] text-plum-700">{data.length} receipts issued</div>
          <h1 className="font-display mt-0.5 text-3xl font-bold leading-tight tracking-tight text-plum-950 sm:text-4xl">History</h1>
        </div>
        <div className="flex w-full flex-wrap gap-2 sm:w-auto">
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search child, parent, grade, receipt no" className="bg-white pl-9" data-testid="history-search-input" />
          </div>
          <Button disabled={!rows.length} onClick={() => exportReceiptsXlsx(rows, `Receipt_History_${new Date().toLocaleDateString("en-CA")}.xlsx`)}
            className="bg-plum-700 hover:bg-plum-800" data-testid="history-export-btn">
            <FileSpreadsheet className="mr-2 h-4 w-4" />Export Excel
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="history-payment-filter">
        <span className="text-xs uppercase tracking-wider text-slate-500">Payment state</span>
        {PS_FILTERS.map((f) => (
          <button key={f.id} onClick={() => setPs(f.id)} data-testid={`history-ps-filter-${f.id}`}
            className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors duration-150 ${ps === f.id ? "border-plum-900 bg-plum-900 text-white" : "border-plum-900/15 bg-white text-slate-600 hover:bg-plum-900/5"}`}>
            {f.label} <span className="font-num opacity-70">{f.id === "all" ? data.length : data.filter((r) => stateKey(verifyCount(r)) === f.id).length}</span>
          </button>
        ))}
      </div>

      <div className="fade-up d1 overflow-x-auto rounded-lg border border-plum-900/10 bg-white">
        <table className="w-full min-w-[900px] text-sm" data-testid="history-table">
          <thead>
            <tr className="border-b border-slate-200 font-bold text-left text-[11px] uppercase tracking-wider text-slate-500">
              <th className="px-4 py-3">Receipt</th><th className="px-4 py-3">Child / Parent</th><th className="px-4 py-3">Grade</th>
              <th className="px-4 py-3">Category</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Payment State</th><th className="px-4 py-3 text-right">At Admission</th>
              <th className="px-4 py-3 text-right">School Fee</th><th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-slate-100 transition-colors duration-150 hover:bg-slate-50" data-testid={`history-row-${r.receipt_no}`}>
                <td className="px-4 py-3">
                  <div className="font-num font-medium">{r.receipt_no}</div>
                  <div className="text-xs text-slate-500">{new Date(r.created_at).toLocaleDateString("en-IN")}</div>
                </td>
                <td className="px-4 py-3"><div className="font-medium">{r.inputs.childName}</div><div className="text-xs text-slate-500">{r.inputs.parentName}</div></td>
                <td className="px-4 py-3"><span className="rounded bg-plum-50 px-2 py-0.5 text-xs font-medium text-plum-800">{r.inputs.grade}</span></td>
                <td className="px-4 py-3"><div>{catLabel(r.inputs)}</div><div className="text-xs text-slate-500">{subLabel(r.inputs.sub)}</div></td>
                <td className="px-4 py-3"><StatusBadge status={r.status} testid={`history-status-${r.receipt_no}`} /></td>
                <td className="px-4 py-3"><PaymentState r={r} testid={`history-payment-state-${r.receipt_no}`} /></td>
                <td className="font-num px-4 py-3 text-right">{inr(r.computed.atAdmission)}</td>
                <td className="font-num px-4 py-3 text-right">{inr(r.computed.schoolFeeAfterDisc)}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-1">
                    <Button size="icon" variant="ghost" onClick={() => nav(`/receipts/${r.id}`)} data-testid={`view-${r.receipt_no}`}><Eye className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => nav(`/?edit=${r.id}`)} data-testid={`edit-${r.receipt_no}`}><Pencil className="h-4 w-4" /></Button>
                    {isAdmin && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="icon" variant="ghost" className="text-red-600 hover:text-red-700" data-testid={`delete-${r.receipt_no}`}><Trash2 className="h-4 w-4" /></Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete {r.receipt_no}?</AlertDialogTitle>
                          <AlertDialogDescription>This permanently removes the receipt for {r.inputs.childName}.</AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={() => del(r.id)} className="bg-red-600 hover:bg-red-700" data-testid="delete-confirm-btn">Delete</AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!isLoading && rows.length === 0 && (
          <div className="flex flex-col items-center gap-2 py-16 text-slate-500" data-testid="history-empty">
            <Inbox className="h-8 w-8" /> No receipts found
          </div>
        )}
      </div>
    </div>
  );
}
